import * as Sentry from '@sentry/react';

/**
 * Error-handling convention (see CLAUDE.md → Key Patterns):
 *
 * 1. Services THROW on true failures — auth errors, validation, DB constraint
 *    violations, failed writes the caller depends on. Callers decide how to
 *    surface them (toast, error state, redirect).
 * 2. Services return null / [] for "not found" / empty results. Never throw
 *    for an empty result set.
 * 3. Best-effort paths (gamification, analytics, welcome emails, dual-writes)
 *    may catch-and-continue, but ONLY via `bestEffort()` or `reportError()`
 *    below — never a bare `console.error`. This keeps degraded features
 *    visible in Sentry instead of silently rotting.
 *
 * `Sentry.captureException` is a safe no-op when Sentry isn't initialised
 * (tests, local dev without VITE_SENTRY_DSN), so these helpers are safe to
 * call from anywhere in client code.
 */

/**
 * Report a caught error to the console AND Sentry, without rethrowing.
 * Use at explicit best-effort boundaries where execution should continue.
 *
 * @param context - where it happened, e.g. 'gamificationService.recordActivity'
 * @param error - the caught error (any shape; non-Errors are wrapped)
 * @param extra - optional structured context for Sentry (ids, inputs)
 */
export function reportError(context: string, error: unknown, extra?: Record<string, unknown>): void {
  console.error(`[${context}]`, error);
  Sentry.captureException(error instanceof Error ? error : new Error(`${context}: ${String((error as any)?.message ?? error)}`), {
    tags: { context },
    extra,
  });
}

/**
 * Run an async operation as an explicit best-effort step: on failure, report
 * the error (console + Sentry) and return the fallback instead of throwing.
 *
 *   const streak = await bestEffort('gamification.recordActivity', () => rpc(...), EMPTY_STREAK);
 */
export async function bestEffort<T>(context: string, fn: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    reportError(context, error);
    return fallback;
  }
}
